/**
 * Connecting a bank through Plaid, from Settings' Bank Connections (C-19,
 * migration 0076; plan-documents/PLAN_plaid_connection_20261013.md).
 *
 * THE CONSENT STEP COMES FIRST, in our own words, before any of Plaid's code
 * is in the page: what Techne Books will receive, that Plaid collects it from
 * the bank under Plaid's End User Privacy Policy (linked), and that
 * disconnecting deletes the token and removes the connection at Plaid. Only
 * Continue To Plaid loads Link (`lib/plaid-link.ts`).
 *
 * THEN, AS MERCURY'S CONNECT DOES, the connection's name, the day the books
 * begin, and the six-digit code from the person's authenticator: the API
 * exchanges Plaid's public token only with the code.
 *
 * MAPPING says the day each account's import starts. The default is the day
 * after the last bank transaction that chart account already holds, which is
 * what keeps a bank connected again after a disconnect from importing anything
 * twice (review PL-7); it is shown before anything is imported.
 *
 * RE-AUTHORISING is Plaid's update mode, for a LIVE connection whose bank asks
 * for it (Reconnect Needed): the same connection, nothing re-imported.
 */

import { useState } from 'react';
import { ApiError, api, type PlaidAccountView, type PlaidMapping } from '../api.js';
import { openPlaidLink } from '../lib/plaid-link.js';
import { formatCents } from '../lib/money.js';
import { formatTimestamp } from '../lib/timezone.js';

function message(caught: unknown, fallback: string): string {
  // Link closing on an error says what failed (review PC-1), by name: see lib/plaid-link.ts.
  if (caught instanceof Error && caught.name === 'PlaidLinkError') return `Plaid stopped: ${caught.message} Nothing was changed; try again.`;
  return caught instanceof ApiError && caught.message !== caught.code ? caught.message : fallback;
}

export const PLAID_PRIVACY_URL = 'https://plaid.com/legal/#end-user-privacy-policy';

/** What the person is told before Plaid opens. */
export function PlaidConsent({ onContinue, onCancel, busy }: { onContinue: () => void; onCancel: () => void; busy: boolean }) {
  return (
    <div className="new-account undo-form" role="group" aria-label="Connect A Bank Through Plaid">
      <p>
        Techne Books uses <strong>Plaid</strong> to connect to your bank. Plaid collects your bank's information when
        you sign in to your bank in Plaid's window, and gives Techne Books the names and last four digits of the
        accounts you choose, their <strong>balances</strong> and their <strong>posted transactions</strong>. Nothing else is
        asked for.
      </p>
      <p>
        Plaid's handling of your information is described in the{' '}
        <a href={PLAID_PRIVACY_URL} target="_blank" rel="noreferrer">
          Plaid End User Privacy Policy
        </a>
        . Disconnecting later deletes the connection's token here and removes the connection at Plaid; the
        transactions already imported stay in your books.
      </p>
      <div className="row">
        <button type="button" disabled={busy} onClick={onContinue}>
          {busy ? 'Opening Plaid…' : 'Continue To Plaid'}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** The whole connect flow: consent, Link, then the name, the start and the code. */
export function PlaidConnectFlow({ onConnected, onCancel }: { onConnected: (connectionId: string) => void; onCancel: () => void }) {
  const [stage, setStage] = useState<'consent' | 'details'>('consent');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicToken, setPublicToken] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [syncStartDate, setSyncStartDate] = useState('');
  const [code, setCode] = useState('');

  async function continueToPlaid() {
    setBusy(true);
    setError(null);
    try {
      const { linkToken } = await api.plaidLinkToken();
      const result = await openPlaidLink(linkToken);
      if (result === null) {
        setBusy(false);
        return;
      }
      setPublicToken(result.publicToken);
      setDisplayName(result.institutionName ?? '');
      setStage('details');
    } catch (caught) {
      setError(message(caught, 'Plaid could not be opened. Try again shortly.'));
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    if (publicToken === null) return;
    setBusy(true);
    setError(null);
    try {
      const connected = await api.connectPlaid({ publicToken, displayName, syncStartDate, code });
      onConnected(connected.connectionId);
    } catch (caught) {
      setError(message(caught, 'The bank was not connected.'));
      setBusy(false);
    }
  }

  return (
    <>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {stage === 'consent' ? (
        <PlaidConsent busy={busy} onCancel={onCancel} onContinue={() => void continueToPlaid()} />
      ) : (
        <div className="new-account undo-form authenticator-form" role="group" aria-label="Finish Connecting">
          <p className="muted">Plaid has connected the bank. Nothing is imported until you finish here and map its accounts.</p>
          <label className="field">
            <span>Connection Name</span>
            <input value={displayName} onChange={(event) => { const next = event.target.value; setDisplayName(next); }} />
          </label>
          <label className="field">
            <span>Books Begin</span>
            <input type="date" value={syncStartDate} onChange={(event) => { const next = event.target.value; setSyncStartDate(next); }} />
          </label>
          <label className="field">
            <span>Code From Your Authenticator</span>
            <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => { const next = event.target.value; setCode(next); }} />
          </label>
          <div className="row">
            <button type="button" disabled={busy} onClick={() => void connect()}>
              {busy ? 'Connecting…' : 'Finish Connecting'}
            </button>
            <button type="button" className="link" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** An amount in its own currency: dollars as the app writes them, any other with its code. */
function inCurrency(cents: number, currency: string): string {
  return currency === 'USD' ? formatCents(cents) : `${formatCents(cents).replace('$', '')} ${currency}`;
}

/**
 * The bank's balance beside the feed's (0077; the Plaid plan's §6). The feed
 * balance counts every transaction the feed brought in, categorized or not, so
 * posting a draft never moves the difference; the uncategorized count says how
 * much is not yet in the books (review PB-1). A card's balance is what is
 * owed, and is shown in the feed's sign, negative, with the owed figure beside
 * it. The difference is given only in dollars, the books' currency (PB-2).
 */
function PlaidBalance({ label, balance }: { label: string; balance: NonNullable<PlaidAccountView['balance']> }) {
  const when = balance.asOf ? formatTimestamp(balance.asOf) : 'a date Plaid did not give';
  const comparison = balance.comparison;
  if (!comparison) {
    return (
      <p className="muted" aria-label={`Balance of ${label}`}>
        <strong>Bank Balance</strong> {inCurrency(balance.currentCents, balance.currency)}, as of {when}.
      </p>
    );
  }
  const owed = comparison.bankCents !== balance.currentCents;
  return (
    <p className="muted" aria-label={`Balances of ${label}`}>
      <strong>Bank Balance</strong> {inCurrency(comparison.bankCents, balance.currency)}
      {owed ? ` (${inCurrency(balance.currentCents, balance.currency)} owed)` : ''}, as of {when}.{' '}
      <strong>Feed Balance</strong> {formatCents(comparison.feedCents)} through {comparison.through}, with{' '}
      {comparison.uncategorized} uncategorized.{' '}
      {comparison.differenceCents === null ? (
        <>No difference is worked out: the bank reports this account in {balance.currency}.</>
      ) : (
        <>
          <strong>Difference</strong> {formatCents(comparison.differenceCents)}.
        </>
      )}
    </p>
  );
}

/** Each Plaid account, the chart account it feeds, and the day its import starts. */
export function PlaidMappingEditor({ connectionId, mapping, onChanged, onDone }: {
  connectionId: string;
  mapping: PlaidMapping;
  onChanged: () => void;
  onDone: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [starts, setStarts] = useState<Record<string, string>>({});

  async function map(plaidAccountId: string, accountId: string | null) {
    setBusy(true);
    setError(null);
    try {
      const chosen = mapping.chart.find((c) => c.accountId === accountId);
      // A date typed and then cleared is '' and means the default, as untouched does.
      const importFrom = accountId === null ? null : (starts[plaidAccountId] || chosen?.importFrom) ?? null;
      await api.setPlaidMapping(connectionId, plaidAccountId, accountId, importFrom);
      onChanged();
    } catch (caught) {
      setError(message(caught, 'That mapping was not saved.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="new-account" role="group" aria-label="Map Plaid Accounts">
      <p className="muted">
        Each account imports onto the chart account you choose, from the day shown. The day defaults to the day
        after the last bank transaction that chart account already holds, so nothing is imported twice.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <ul className="history-list plaid-mapping" aria-label="Plaid Accounts">
        {mapping.accounts.map((account) => {
          const label = `${account.name}${account.mask ? ` ••${account.mask}` : ''}`;
          const selected = account.mappedTo?.accountId ?? '';
          const defaultStart = mapping.chart.find((c) => c.accountId === selected)?.importFrom ?? '';
          return (
            <li key={account.plaidAccountId}>
              <label className="field">
                <span>{label}</span>
                <select
                  aria-label={`Chart account for ${label}`}
                  value={selected}
                  disabled={busy}
                  onChange={(event) => {
                    const next = event.target.value;
                    void map(account.plaidAccountId, next === '' ? null : next);
                  }}
                >
                  <option value="">Not Imported</option>
                  {mapping.chart.map((chart) => (
                    <option key={chart.accountId} value={chart.accountId}>
                      {chart.code} {chart.name} (imports from {chart.importFrom})
                    </option>
                  ))}
                </select>
              </label>
              {account.mappedTo && (
                <p className="muted">
                  Imports from <strong>{account.mappedTo.importFrom}</strong>.{' '}
                  {defaultStart !== '' && defaultStart !== account.mappedTo.importFrom ? `(The default is ${defaultStart}.)` : ''}
                </p>
              )}
              {account.balance && <PlaidBalance label={label} balance={account.balance} />}
              {!account.mappedTo && (
                <label className="field">
                  <span>Import From</span>
                  <input
                    type="date"
                    aria-label={`Import from, for ${label}`}
                    value={starts[account.plaidAccountId] ?? ''}
                    placeholder="the default"
                    onChange={(event) => {
                      const next = event.target.value;
                      setStarts((current) => ({ ...current, [account.plaidAccountId]: next }));
                    }}
                  />
                </label>
              )}
            </li>
          );
        })}
      </ul>
      <button type="button" className="link" onClick={onDone}>
        Done
      </button>
    </div>
  );
}

/** Update mode for a live connection whose bank asks to be re-authorised. */
export function PlaidReauthorise({ connectionId, displayName, onDone, onCancel }: {
  connectionId: string;
  displayName: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reauthorise() {
    setBusy(true);
    setError(null);
    try {
      const { linkToken } = await api.plaidUpdateLinkToken(connectionId, code);
      const result = await openPlaidLink(linkToken);
      if (result !== null) onDone();
    } catch (caught) {
      setError(message(caught, 'Plaid could not be opened. Try again shortly.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="new-account undo-form authenticator-form" role="group" aria-label={`Re-Authorise ${displayName}`}>
      <p>
        The bank asks you to sign in to it again through Plaid. This keeps the same connection: nothing is imported
        twice.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <label className="field">
        <span>Code From Your Authenticator</span>
        <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => { const next = event.target.value; setCode(next); }} />
      </label>
      <div className="row">
        <button type="button" disabled={busy} onClick={() => void reauthorise()}>
          {busy ? 'Opening Plaid…' : 'Re-Authorise'}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
